import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CompanyFilter } from '../components/CompanyFilter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Download, TrendingDown, AlertTriangle, Zap, Target, Loader2, Brain } from 'lucide-react';

function ConfBadge({ level }) {
  const cls = level === 'high' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : level === 'medium' ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-slate-100 text-slate-400 border-slate-200';
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${cls}`}>{level}</span>;
}

export default function SavingsAgentPage() {
  const [data, setData] = useState(null);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [digestLoading, setDigestLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [filterCompany, setFilterCompany] = useState('all');

  const fetchData = () => {
    const p = {};
    if (filterCompany !== 'all') p.company_id = filterCompany;
    const qs = new URLSearchParams(p).toString();
    api.get(`/savings-agent/full-analysis?${qs}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { api.get('/companies').then(r => setCompanies(r.data)).catch(()=>{}); fetchData(); }, []);
  useEffect(() => { setLoading(true); fetchData(); }, [filterCompany]);

  const loadDigest = async () => {
    setDigestLoading(true);
    try {
      const p = {};
      if (filterCompany !== 'all') p.company_id = filterCompany;
      const res = await api.get(`/savings-agent/weekly-digest?${new URLSearchParams(p)}`);
      setDigest(res.data);
    } catch { toast.error('Failed to load digest'); }
    finally { setDigestLoading(false); }
  };

  const s = data?.summary || {};

  return (
    <div data-testid="savings-agent-page">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2"><Brain size={24} className="text-orange-600" />AI Savings Intelligence</h1>
          <p className="text-sm text-slate-500 mt-1">Multi-dimensional cost analysis and saving opportunity identification</p>
        </div>
        <Button variant="outline" onClick={loadDigest} disabled={digestLoading} data-testid="load-digest-btn">
          {digestLoading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Download size={14} className="mr-1.5" />}Weekly Digest
        </Button>
      </div>

      <div className="flex gap-3 mb-6">
        <CompanyFilter companies={companies} value={filterCompany} onChange={setFilterCompany} />
      </div>

      {loading ? <div className="animate-pulse space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-slate-100 rounded-lg" />)}</div> : !data || data.status === 'no_data' ? (
        <Card className="bg-white border border-slate-200 rounded-lg p-12 text-center"><Target size={48} className="mx-auto text-slate-200 mb-4" /><p className="text-slate-500">No operational data available for savings analysis. Record tooth point installations and removals to enable AI savings identification.</p></Card>
      ) : (
        <>
          {/* Summary KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <Card className="stat-card border-orange-200 bg-orange-50/30"><p className="text-xs font-semibold uppercase tracking-wider text-orange-600">Total Savings Identified</p><p className="text-2xl font-bold text-orange-700 mt-1 font-heading">₹{(s.total_savings_identified || 0).toLocaleString()}</p></Card>
            <Card className="stat-card border-emerald-200 bg-emerald-50/30"><p className="text-xs font-semibold uppercase tracking-wider text-emerald-600">Projected Annual Savings</p><p className="text-2xl font-bold text-emerald-700 mt-1 font-heading">₹{(s.projected_annual_savings || 0).toLocaleString()}</p></Card>
            <Card className="stat-card"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Best Product</p><p className="text-lg font-bold text-slate-900 mt-1 font-heading">{s.best_product || '-'}</p><p className="text-xs text-slate-400">₹{s.best_cph?.toFixed(2) || '0.00'}/hr</p></Card>
            <Card className="stat-card"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">False Economies</p><p className="text-2xl font-bold text-red-600 mt-1 font-heading">{s.false_economies_detected || 0}</p></Card>
            <Card className="stat-card"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Machine Hotspots</p><p className="text-2xl font-bold text-amber-600 mt-1 font-heading">{s.machine_hotspots || 0}</p></Card>
          </div>

          {/* Key Insights */}
          {(data.insights || []).length > 0 && (
            <div className="space-y-3 mb-8">
              {data.insights.map((ins, i) => (
                <Card key={i} className={`p-4 border ${ins.priority === 'high' ? 'border-orange-200 bg-orange-50/20' : 'border-slate-200'}`}>
                  <div className="flex items-start gap-3">
                    <Zap size={16} className={`mt-0.5 flex-shrink-0 ${ins.priority === 'high' ? 'text-orange-600' : 'text-slate-400'}`} />
                    <div><p className="text-sm font-semibold text-slate-900">{ins.title}</p><p className="text-sm text-slate-600 mt-0.5">{ins.text}</p>{ins.saving > 0 && <p className="text-xs font-medium text-emerald-600 mt-1">Potential saving: ₹{ins.saving.toLocaleString()}</p>}</div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Product Switch Opportunities */}
          {(data.product_opportunities || []).length > 0 && (
            <Card className="bg-white border border-slate-200 rounded-lg mb-6">
              <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2"><TrendingDown size={16} className="text-orange-600" /><h3 className="text-sm font-semibold text-slate-900 font-heading">Tooth Point Switch Opportunities</h3></div>
              <div className="data-table-wrapper border-0 shadow-none rounded-none">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Current Product</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cost/Hr</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Recommended</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Rec. Cost/Hr</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Saving/Hr</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Saving</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Confidence</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.product_opportunities.map((o, i) => (
                      <TableRow key={i} className="hover:bg-slate-50/50">
                        <TableCell className="font-medium text-slate-900">{o.current_product}</TableCell>
                        <TableCell className="text-sm text-red-600 font-medium">₹{o.current_cph?.toFixed(2)}</TableCell>
                        <TableCell className="font-medium text-emerald-700">{o.recommended_product}</TableCell>
                        <TableCell className="text-sm text-emerald-600 font-medium">₹{o.recommended_cph?.toFixed(2)}</TableCell>
                        <TableCell className="text-sm font-bold text-orange-600">₹{o.saving_per_hour?.toFixed(2)}</TableCell>
                        <TableCell className="text-sm font-bold text-slate-900">₹{o.total_saving?.toLocaleString()}</TableCell>
                        <TableCell><ConfBadge level={o.confidence} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {data.product_opportunities.length > 0 && <div className="px-5 py-3 border-t border-slate-100"><p className="text-xs text-slate-500">{data.product_opportunities[0]?.reason}</p></div>}
            </Card>
          )}

          {/* False Economies */}
          {(data.false_economies || []).length > 0 && (
            <Card className="bg-white border border-red-100 rounded-lg mb-6">
              <div className="px-5 py-3 border-b border-red-50 flex items-center gap-2"><AlertTriangle size={16} className="text-red-500" /><h3 className="text-sm font-semibold text-slate-900 font-heading">False Economy Alerts</h3></div>
              <div className="p-5 space-y-3">
                {data.false_economies.map((fe, i) => (
                  <div key={i} className="bg-red-50/50 rounded-lg p-3 border border-red-100">
                    <p className="text-sm text-slate-800">{fe.reason}</p>
                    <div className="flex gap-4 mt-2"><ConfBadge level={fe.confidence} /></div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Weekly Digest */}
          {digest && (
            <Card className="bg-white border border-slate-200 rounded-lg p-6 mt-6" data-testid="weekly-digest">
              <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Weekly Cost Intelligence Digest</h3>
              <p className="text-xs text-slate-400 mb-4">Generated: {new Date(digest.generated_at).toLocaleString()}</p>
              <div className="space-y-6">
                {(digest.sections || []).map((sec, i) => (
                  <div key={i}>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{sec.title}</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {sec.metrics.map((m, j) => (
                        <div key={j} className="bg-slate-50 rounded p-2.5"><p className="text-xs text-slate-500">{m.label}</p><p className="text-sm font-bold text-slate-900">{m.value}</p></div>
                      ))}
                    </div>
                  </div>
                ))}
                {(digest.anomalies || []).length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-red-500 mb-2">Anomalies</h4>
                    {digest.anomalies.map((a, i) => <p key={i} className="text-sm text-red-700 mb-1">{a}</p>)}
                  </div>
                )}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
